'use client'

import * as NextShare from 'next-share'
import {Dispatch, SetStateAction} from 'react'
import {createPortal} from 'react-dom'
import {useEffect, useState} from 'react'
import clsx from 'clsx'
import copy from 'copy-text-to-clipboard'
import s from './index.module.scss'
import CopyIcon from './copy-icon'
import {SwipeableHandlers, useSwipeable} from 'react-swipeable'
import Backdrop from '@app/components/header/sections/desktop/search/backdrop/backdrop'

const socialsList = ['Facebook', 'Whatsapp', 'X', 'Linkedin', 'Telegram', 'Reddit', 'Email'] as const
const componentNameMap = {
  X: 'Twitter',
} as const

type Props = {
  shareData: {
    url: string
  }
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
}

export default function SharePopup({shareData, isOpen, setIsOpen}: Props) {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    // Ensure component is mounted before using portal for animations
    setMounted(true)
  }, [])

  const renderShareButton = (option: (typeof socialsList)[number]) => {
    const componentName = componentNameMap[option as keyof typeof componentNameMap] || option

    const ShareButton = NextShare[`${componentName}ShareButton`]
    const ShareIcon = NextShare[`${componentName}Icon`]

    return (
      <ShareButton key={option} url={shareData.url} blankTarget onClick={() => setIsOpen(false)}>
        <ShareIcon round />
        <span className={s.text}>{option}</span>
      </ShareButton>
    )
  }

  const handlers: SwipeableHandlers = useSwipeable({
    onSwipedDown: () => setIsOpen(false),
    trackMouse: true,
  })

  const handleCopy = () => {
    copy(shareData.url)
    setIsOpen(false)
  }

  const modalContent = (
    <div className={clsx(s.overlay, {[s.isOpen]: isOpen})} {...handlers}>
      <Backdrop onClick={() => setIsOpen(false)} showBackdrop={isOpen} />
      <div className={clsx(s.popup, {[s.isOpen]: isOpen})}>
        <div className='flex justify-center mb-10 md:hidden lg:hidden'>
          <div className='w-40 h-5 bg-slate-400 rounded-full' />
        </div>
        <div className={s.title}>
          <span className={s.share}>Share</span>
          <button className='rounded-full p-4 px-10 text-3xl' onClick={() => setIsOpen(false)}>
            ✕
          </button>
        </div>
        <div className={s.options}>
          <button className={s.copyButton} onClick={handleCopy}>
            <CopyIcon />
            <span className={s.text}>Copy</span>
          </button>
          {socialsList.map(renderShareButton)}
        </div>
      </div>
    </div>
  )

  return mounted ? createPortal(modalContent, document.body) : null
}
